import { useCallback, useEffect, useId, useState } from 'react'
import { Alert, AlertDialog, Button, Chip, Input, Label, Modal, Table, TextField } from '@heroui/react'
import { ChoiceChips } from '../admin/peopleKit'
import { useFlash } from '../admin/sectionKit'
import { IconTile, ListCard, Notice } from '../admin/settingsKit'
import { api } from '../lib/api'
import {
  claudeCommand,
  DEFAULT_EXPIRY,
  EXPIRY_CHOICES,
  shownTokens,
  tokenDate,
  tokenState,
  type ExpiryDays,
} from '../lib/accessTokens'
import { explain } from '../lib/staffErrors'
import { formatMoment } from '../lib/staffText'
import type { AccessToken } from '../lib/types'
import { Icon } from '../ui/icons'
import { EmptyState } from '../ui/parts'
import { useStaffText } from '../ui/StaffText'
import '../styles/settings.css'
import '../styles/access.css'

/** One person's token list: read, revoke one, read again. */
function useTokens(read: () => Promise<{ tokens: AccessToken[] }>, revoke: (id: string) => Promise<void>) {
  const { lang, say } = useStaffText()
  const { flash, toast } = useFlash()
  const [tokens, setTokens] = useState<AccessToken[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState('')
  const load = useCallback(async () => setTokens((await read()).tokens), [read])

  useEffect(() => {
    load().catch((err: unknown) => {
      setTokens([])
      setError(err)
    })
  }, [load])

  async function revokeOne(id: string) {
    setBusy(true)
    setError(null)
    try {
      await revoke(id)
      setAsking('')
      await load()
      flash(say('tokens.revoked'))
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const errorText = error === null ? '' : explain(error, lang, 'common.unreachable')
  return { tokens, errorText, busy, asking, setAsking, revokeOne, load, flash, toast }
}

/** Account › Access tokens: the signed-in person's own, with New token. */
export function AccountTokens() {
  const { lang, say } = useStaffText()
  const read = useCallback(() => api.accessTokens(), [])
  const revoke = useCallback((id: string) => api.revokeAccessToken(id), [])
  const { tokens, errorText, busy, asking, setAsking, revokeOne, load, flash, toast } = useTokens(read, revoke)
  const [creating, setCreating] = useState(false)
  const now = Date.now()
  const shown = shownTokens(tokens ?? [], now)
  const active = shown.filter((t) => tokenState(t, now) === 'active').length
  const askingFor = shown.find((t) => t.id === asking)

  return (
    <>
      {errorText && <Notice>{errorText}</Notice>}
      <ListCard
        title={say('tokens.title')}
        count={say('tokens.count', { n: active })}
        action={
          <Button variant="primary" size="sm" onPress={() => setCreating(true)}>
            <Icon name="plus" size={16} /> {say('tokens.new')}
          </Button>
        }
      >
        {tokens === null ? null : shown.length === 0 ? (
          <EmptyState icon={<Icon name="key" size={20} />} title={say('tokens.none')} />
        ) : (
          <Table variant="secondary" className="atok-table">
            <Table.ScrollContainer>
              <Table.Content aria-label={say('tokens.title')}>
                <Table.Header>
                  <Table.Column isRowHeader>{say('tokens.col.name')}</Table.Column>
                  <Table.Column>{say('tokens.col.created')}</Table.Column>
                  <Table.Column>{say('tokens.col.expires')}</Table.Column>
                  <Table.Column>{say('tokens.col.lastUsed')}</Table.Column>
                  <Table.Column>{say('tokens.col.state')}</Table.Column>
                  <Table.Column className="atok-act">
                    <span className="sr">{say('tokens.revoke')}</span>
                  </Table.Column>
                </Table.Header>
                <Table.Body>
                  {shown.map((t) => {
                    const state = tokenState(t, now)
                    return (
                      <Table.Row key={t.id} id={t.id} className={state === 'active' ? undefined : 'atok-off'}>
                        <Table.Cell>
                          <span className="atok-name">
                            <IconTile icon="key" tone={2} size="sm" />
                            <span className="atok-name__text">
                              <b>{t.name}</b>
                              <small className="atok-hint">{t.hint}</small>
                            </span>
                          </span>
                        </Table.Cell>
                        <Table.Cell>
                          <span className="set-muted">{tokenDate(t.createdAt, lang, now)}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <span className="set-muted">{t.expiresAt ? tokenDate(t.expiresAt, lang, now) : say('tokens.noExpiry')}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <span className="atok-used">
                            <span>{t.lastUsedAt ? formatMoment(t.lastUsedAt, lang, { date: true, time: true }) : say('tokens.never')}</span>
                            {t.lastUsedIp && <small>{t.lastUsedIp}</small>}
                          </span>
                        </Table.Cell>
                        <Table.Cell>
                          <Chip size="sm" variant="soft" color={state === 'active' ? 'success' : 'default'}>
                            {say(`tokens.state.${state}`)}
                          </Chip>
                        </Table.Cell>
                        <Table.Cell className="atok-act">
                          {state === 'active' && (
                            <Button variant="danger-soft" size="sm" aria-label={`${say('tokens.revoke')} ${t.name}`} onPress={() => setAsking(t.id)}>
                              {say('tokens.revoke')}
                            </Button>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    )
                  })}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>
        )}
      </ListCard>
      <AlertDialog.Backdrop isOpen={askingFor !== undefined} onOpenChange={(open) => !open && setAsking('')}>
        <AlertDialog.Container size="sm">
          <AlertDialog.Dialog>
            <AlertDialog.Header>
              <AlertDialog.Icon status="danger" />
              <AlertDialog.Heading>{say('tokens.revokeAsk', { name: askingFor?.name ?? '' })}</AlertDialog.Heading>
            </AlertDialog.Header>
            <AlertDialog.Footer>
              <Button variant="tertiary" onPress={() => setAsking('')}>
                {say('common.cancel')}
              </Button>
              <Button variant="danger" isDisabled={busy} onPress={() => askingFor && void revokeOne(askingFor.id)}>
                {say('tokens.revoke')}
              </Button>
            </AlertDialog.Footer>
          </AlertDialog.Dialog>
        </AlertDialog.Container>
      </AlertDialog.Backdrop>
      {creating && (
        <NewTokenDialog
          onClose={() => setCreating(false)}
          onCopied={() => flash(say('tokens.copied'))}
          onMade={() => void load().catch(() => {})}
        />
      )}
      {toast}
    </>
  )
}

/** The person card's tokens: the working ones, each with Revoke for an administrator. */
export function PersonTokens({ staffId, readOnly }: { staffId: string; readOnly: boolean }) {
  const { lang, say } = useStaffText()
  const read = useCallback(() => api.staffAccessTokens(staffId), [staffId])
  const revoke = useCallback((id: string) => api.revokeStaffAccessToken(staffId, id), [staffId])
  const { tokens, errorText, busy, asking, setAsking, revokeOne, toast } = useTokens(read, revoke)
  const now = Date.now()
  const active = (tokens ?? []).filter((t) => tokenState(t, now) === 'active')

  return (
    <div className="field atok-person" role="group" aria-label={say('tokens.title')}>
      <span>{say('tokens.title')}</span>
      {errorText && <small className="atok-error">{errorText}</small>}
      {tokens !== null &&
        (active.length === 0 ? (
          <small>{say('tokens.personNone')}</small>
        ) : (
          <ul className="atok-list">
            {active.map((t) => (
              <li key={t.id} className="atok-row">
                <IconTile icon="key" tone={2} size="sm" />
                <span className="atok-row__text">
                  <b>{t.name}</b>
                  <small>
                    {[
                      t.hint,
                      t.lastUsedAt
                        ? say('tokens.lastUsedOn', { when: formatMoment(t.lastUsedAt, lang, { date: true, time: true }) })
                        : say('tokens.neverUsed'),
                      t.expiresAt ? say('tokens.expiresOn', { when: tokenDate(t.expiresAt, lang, now) }) : say('tokens.noExpiry'),
                    ].join(' · ')}
                  </small>
                </span>
                {!readOnly && (
                  <Revoke
                    token={t}
                    open={asking === t.id}
                    busy={busy}
                    onAsk={() => setAsking(t.id)}
                    onNo={() => setAsking('')}
                    onYes={() => void revokeOne(t.id)}
                  />
                )}
              </li>
            ))}
          </ul>
        ))}
      {toast}
    </div>
  )
}

/** Revoke asked in place: the person card sits in a sheet, where a second dialog would fight its focus. */
function Revoke({
  token,
  open,
  busy,
  onAsk,
  onNo,
  onYes,
}: {
  token: AccessToken
  open: boolean
  busy: boolean
  onAsk: () => void
  onNo: () => void
  onYes: () => void
}) {
  const { say } = useStaffText()
  if (!open) {
    return (
      <Button variant="ghost" size="sm" onPress={onAsk}>
        {say('tokens.revoke')}
      </Button>
    )
  }
  return (
    <span className="atok-ask" role="alertdialog" aria-label={say('tokens.revoke')}>
      <span>{say('tokens.revokeAsk', { name: token.name })}</span>
      <Button variant="ghost" size="sm" onPress={onNo}>
        {say('common.cancel')}
      </Button>
      <Button variant="danger" size="sm" isDisabled={busy} onPress={onYes}>
        {say('tokens.revoke')}
      </Button>
    </span>
  )
}

function NewTokenDialog({ onClose, onCopied, onMade }: { onClose: () => void; onCopied: () => void; onMade: () => void }) {
  const { lang, say } = useStaffText()
  const formId = useId()
  const [name, setName] = useState('')
  const [expiry, setExpiry] = useState<ExpiryDays>(DEFAULT_EXPIRY)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [made, setMade] = useState<{ name: string; secret: string } | null>(null)
  const [asking, setAsking] = useState(false)
  const valid = name.trim() !== ''
  // A typed name is asked about before it is dropped; the shown secret closes at once.
  const close = () => (!made && valid ? setAsking(true) : onClose())

  async function create() {
    const clean = name.trim()
    if (!clean) return
    setBusy(true)
    setError('')
    try {
      const answer = await api.createAccessToken(clean, expiry)
      setMade({ name: answer.token.name, secret: answer.secret })
      onMade()
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  const choices = EXPIRY_CHOICES.map((d) => ({
    value: String(d),
    label: d === null ? say('tokens.noExpiry') : say('tokens.days', { n: d }),
  }))

  return (
    <Modal.Backdrop isOpen onOpenChange={(open) => !open && close()}>
      <Modal.Container size="md" scroll="inside">
        <Modal.Dialog>
          <Modal.CloseTrigger aria-label={say('ui.close')} />
          <Modal.Header>
            <Modal.Heading title={made ? undefined : say('tokens.actsAsYou')}>{made ? say('tokens.created') : say('tokens.newTitle')}</Modal.Heading>
          </Modal.Header>
          <Modal.Body>
            {made ? (
              <div className="atok-form">
                <Alert status="warning">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title>{say('tokens.shownOnce')}</Alert.Title>
                  </Alert.Content>
                </Alert>
                <Copyable label={say('tokens.secret')} value={made.secret} onCopied={onCopied} />
                <Copyable label={say('tokens.command')} value={claudeCommand(window.location.origin, made.secret)} onCopied={onCopied} />
              </div>
            ) : (
              <form
                id={formId}
                className="atok-form"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (valid && !busy) void create()
                }}
              >
                {error && <Notice>{error}</Notice>}
                <TextField className="atok-field" value={name} onChange={setName} fullWidth>
                  <Label>{say('tokens.name')}</Label>
                  <Input maxLength={100} autoFocus autoComplete="off" placeholder={say('tokens.namePlaceholder')} />
                </TextField>
                <div className="atok-field">
                  <span className="atok-label">{say('tokens.expiry')}</span>
                  <ChoiceChips
                    name="token-expiry"
                    label={say('tokens.expiry')}
                    options={choices}
                    value={String(expiry)}
                    onChange={(v) => setExpiry(v === 'null' ? null : (Number(v) as ExpiryDays))}
                  />
                </div>
              </form>
            )}
          </Modal.Body>
          <Modal.Footer>
            {asking ? (
              <span className="atok-ask" role="alertdialog" aria-label={say('ui.discardAsk')}>
                <strong>{say('ui.discardAsk')}</strong>
                <Button variant="tertiary" size="sm" autoFocus onPress={() => setAsking(false)}>
                  {say('ui.keepEditing')}
                </Button>
                <Button variant="danger" size="sm" onPress={onClose}>
                  {say('ui.discard')}
                </Button>
              </span>
            ) : made ? (
              <Button variant="primary" size="sm" onPress={onClose}>
                {say('ui.close')}
              </Button>
            ) : (
              <>
                <Button variant="tertiary" size="sm" onPress={close}>
                  {say('common.cancel')}
                </Button>
                <Button type="submit" form={formId} variant="primary" size="sm" isDisabled={!valid || busy}>
                  {busy ? say('common.saving') : say('tokens.create')}
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}

/** A value to copy; selectable by hand where the clipboard is refused (plain http). */
function Copyable({ label, value, onCopied }: { label: string; value: string; onCopied: () => void }) {
  const { say } = useStaffText()
  const canCopy = typeof navigator !== 'undefined' && Boolean(navigator.clipboard)
  return (
    <div className="atok-field" role="group" aria-label={label}>
      <span className="atok-label">{label}</span>
      <div className="atok-copy">
        <code>{value}</code>
        {canCopy && (
          <Button variant="secondary" size="sm" onPress={() => void navigator.clipboard.writeText(value).then(onCopied, () => {})}>
            <Icon name="copy" size={15} /> {say('tokens.copy')}
          </Button>
        )}
      </div>
    </div>
  )
}
